import { useCallback } from 'react';

import { useSnackbar } from 'notistack';

import { MAX_PINNED_LENSES } from './lensPins';
import { useLensPins } from './useLensPins';

/** Drops ids of Lenses that are not in the loaded list, so a deleted Lens never holds a pin slot. */
export function prunePinnedIds(ids: Array<string>, knownIds: Array<string>): Array<string> {
  const known = new Set(knownIds);
  return ids.filter((id) => known.has(id));
}

/**
 * Pin or unpin a Lens from any surface. Every surface shares one store (the reader's own browser
 * storage), so a pin made on a tile shows in the rail at once. Past the cap the pin is refused
 * with a one-line notice instead of silently dropping another pin.
 */
export function useLensPinToggle(knownLensIds?: Array<string>) {
  const [storedIds, setPinnedIds] = useLensPins();
  // With the loaded Lens ids, dead pins stop counting toward the cap and stop rendering. The
  // stored list stays intact so a Lens that comes back is still pinned.
  const pinnedIds = knownLensIds ? prunePinnedIds(storedIds, knownLensIds) : storedIds;
  const { enqueueSnackbar } = useSnackbar();
  const toggle = useCallback(
    (lensId: string) => {
      if (pinnedIds.includes(lensId)) {
        setPinnedIds((current) => current.filter((id) => id !== lensId));
        return;
      }
      if (pinnedIds.length >= MAX_PINNED_LENSES) {
        enqueueSnackbar('Unpin one to pin another.', { variant: 'info' });
        return;
      }
      setPinnedIds((current) => [...current, lensId]);
    },
    [enqueueSnackbar, pinnedIds, setPinnedIds],
  );
  return { isPinned: (lensId: string) => pinnedIds.includes(lensId), pinnedIds, toggle };
}
